import { MIDTERMS_PARTNER_ID_CHARACTERS } from '../../lib/midtermsEmbedParams';
import type { AnyField } from '../fields';
import type { WidgetSchema } from '../types';

/**
 * 2026 midterms forecast map: Senate, House, Governor, or Balance of Power (the
 * KPOW chart). One widget with a map picker rather than four, because every
 * option shares the same contract (see lib/midtermsEmbedParams.ts): link mode,
 * forecast / timeline toggles, partner attribution, and the always-on Kalshi
 * brand bar, which deliberately has no toggle.
 */

const MAP_OPTIONS = [
    { value: 'senate', label: 'Senate' },
    { value: 'house', label: 'House' },
    { value: 'governor', label: 'Governor' },
    { value: 'balance-of-power', label: 'Balance of Power (KPOW)' },
];

const fields: AnyField[] = [
    { kind: 'enum', key: 'map', label: 'Map', defaultValue: 'senate', control: 'select', options: MAP_OPTIONS },
    {
        kind: 'enum',
        key: 'links',
        label: 'Market links',
        description: 'Informational embeds show the same map and odds with nothing to click through.',
        defaultValue: 'new-tab',
        control: 'segmented',
        options: [
            { value: 'new-tab', label: 'Open in new tab' },
            { value: 'none', label: 'Informational' },
        ],
    },
    { kind: 'boolean', key: 'forecast', label: 'Show forecast card', defaultValue: true },
    // Always rendered: the schema crosses a Server→Client boundary, so no `visibleWhen`
    // predicate. The Balance of Power embed simply ignores it (it has no timeline).
    { kind: 'boolean', key: 'timeline', label: 'Show timeline slider (maps only)', defaultValue: false },
    { kind: 'theme', key: 'colorTheme', label: 'Theme', defaultValue: 'light' },
    {
        kind: 'dimensions',
        key: 'dimensions',
        label: 'Dimensions',
        defaultValue: { width: '100%', height: '760px' },
    },
    {
        kind: 'text',
        key: 'partner',
        label: 'Partner ID',
        description:
            'Optional. Attributes views and clicks to your site in Kalshi analytics. Letters, numbers, "-", "_" and "." only.',
        defaultValue: '',
        placeholder: 'e.g. acme-news',
        maxLength: 100,
        allowedCharacters: MIDTERMS_PARTNER_ID_CHARACTERS,
    },
    {
        kind: 'utm',
        key: 'utm',
        label: 'Marketing (UTM)',
        defaultValue: { utmSource: '', utmCampaign: '', promoCode: '' },
    },
    { kind: 'boolean', key: 'showBorder', label: 'Border', defaultValue: false },
    { kind: 'boolean', key: 'rounded', label: 'Rounded corners', defaultValue: false },
];

export const midtermsMap: WidgetSchema = {
    meta: {
        id: 'midterms-map',
        category: 'elections',
        categoryTitle: 'Elections',
        title: '2026 Midterms Forecast Map',
        description:
            'Live, market-implied odds for every 2026 Senate, House and Governor race, or the American Power Index.',
        status: 'live',
    },
    fields,
    preview: { kind: 'direct-iframe' },
    embed: {
        kind: 'direct-iframe',
        fixed: {},
        iframePath: '/external-widgets/midterms',
        iframePathParam: 'map',
    },
    fullscreenUrl: 'https://kalshi.com/category/elections/midterms',
};
